import {
  isBlockFact,
  isContextParameterFact
} from '@epilot/journey-logic-commons/src/next/references'
import type {
  LOGIC_TRIGGER,
  LogicId,
  Logic,
  LOGIC_ACTION,
  ComparisonValue,
  Clause
} from '@epilot/journey-logic-commons/src/types/next'
import {
  AVAILABILITY_CHECK_FUNCTIONS,
  LOGIC_OPERATOR,
  PRODUCTS_FUNCTIONS
} from '@epilot/journey-logic-commons/src/types/next'
import { JSONPath } from 'jsonpath-plus'
import pEvery from 'p-every'
import pLimit from 'p-limit'
import pOne from 'p-one'
import { isString } from 'radashi'
import { z, ZodError } from 'zod'

import type { Executor } from './executor'
import { DisplayConditionExecutor } from './executors/displayConditionExecutor'
import { JumpToStepExecutor } from './executors/jumpToStepExecutor'
import { SkipStepExecutor } from './executors/skipStepExecutor'
import { availabilityCheckFunction } from './functions/availabilityCheck'
import { productAvailabilityCheckFunction } from './functions/productAvailabilityCheck'
import { productSelectionFunction } from './functions/productSelection'
import type { Operator } from './operator'
import { Between } from './operators/between'
import { Contain } from './operators/contain'
import { ContainEither } from './operators/containEither'
import { CustomFunction } from './operators/customFunction'
import { Empty } from './operators/empty'
import { EndsWith } from './operators/endsWith'
import { Equal } from './operators/equal'
import { GreaterThan } from './operators/greaterThan'
import { GreaterThanOrEqual } from './operators/greaterThanOrEqual'
import { In } from './operators/in'
import { LessThan } from './operators/lessThan'
import { LessThanOrEqual } from './operators/lessThanOrEqual'
import { Match } from './operators/match'
import { NotContain } from './operators/notContain'
import { NotEmpty } from './operators/notEmpty'
import { NotEqual } from './operators/notEqual'
import { NotIn } from './operators/notIn'
import { NotMatch } from './operators/notMatch'
import { StartsWith } from './operators/startsWith'
import type {
  EngineOptions,
  EnrichedRuntimeContext,
  FunctionHandler,
  LogicEvent,
  RuntimeContext,
  RuntimeFacts
} from './types'

const engineOptionsSchema = z.object({
  concurrency: z.number().finite().min(1).optional().default(5),
  operatorTimeoutMs: z.number().finite().optional().default(3_000)
})

export class Engine {
  /**
   * Options for the engine.
   * Used to configure the engine's behavior per instance.
   */
  protected readonly options: Required<EngineOptions>

  /**
   * Ordered array of logics by their priority, from higher to lower.
   * Logics priority are implicitly defined according to their action type,
   * which is defined by the executor.
   */
  protected prioritized: Array<Set<LogicId>> = []

  /**
   * Stores associated logic expressions within the engine.
   * This allows us to serve our purposes fully detached from other parts of the system like the store.
   */
  protected logicsCache: Map<LogicId, Logic> = new Map()

  /**
   * Static map of executors by action type.
   */
  protected static executors = new Map<LOGIC_ACTION, Executor>(
    [
      new JumpToStepExecutor(),
      new SkipStepExecutor(),
      new DisplayConditionExecutor()
    ].map((executor) => [executor.name, executor])
  )

  /**
   * Static map of operators.
   */
  protected static operators = new Map<LOGIC_OPERATOR, Operator>(
    [
      new Equal(),
      new NotEqual(),
      new GreaterThan(),
      new GreaterThanOrEqual(),
      new LessThan(),
      new LessThanOrEqual(),
      new In(),
      new NotIn(),
      new Contain(),
      new NotContain(),
      new ContainEither(),
      new StartsWith(),
      new EndsWith(),
      new Empty(),
      new NotEmpty(),
      new Between(),
      new Match(),
      new NotMatch()
    ].map((operator) => [operator.name, operator])
  )

  /**
   * Static map of epilot-managed custom functions.
   *
   * These functions are used to extend the logic engine with custom functionality
   * that is required by specific blocks or features.
   */
  protected customFunctions = new Map<string, CustomFunction>([
    [
      AVAILABILITY_CHECK_FUNCTIONS.AVAILABILITY_CHECK,
      availabilityCheckFunction
    ],
    [
      AVAILABILITY_CHECK_FUNCTIONS.PRODUCT_AVAILABILITY_CHECK,
      productAvailabilityCheckFunction
    ],
    [PRODUCTS_FUNCTIONS.PRODUCT_SELECTION, productSelectionFunction]
  ])

  constructor(options?: EngineOptions) {
    this.options = engineOptionsSchema.parse(options ?? {})
  }

  static executor(type: LOGIC_ACTION): Executor {
    const executor = Engine.executors.get(type)

    if (!executor) {
      throw new Error(`No executor found for action '${type}'`)
    }

    return executor
  }

  operator(type: LOGIC_OPERATOR, functionName?: string): Operator {
    let operator: Operator | undefined

    if (type === LOGIC_OPERATOR.CUSTOM_FUNCTION) {
      operator = this.customFunctions.get(functionName ?? '__unknown__')

      if (!operator) {
        throw new Error(`No function operator found for '${functionName}'`)
      }
    } else {
      operator = Engine.operators.get(type)

      if (!operator) {
        throw new Error(`No operator found for '${type}'`)
      }
    }

    return operator
  }

  /**
   * Parses through logics and computes a prioritized list references to them.
   * TODO: consider validating logics against their schema
   */
  ingestLogics(items: Logic[]) {
    this.logicsCache = new Map()
    this.prioritized = []

    const logicsByPriority: Record<number, Set<LogicId>> = {}

    for (const item of items) {
      const executor = Engine.executor(item.action)
      const priority = executor.priority(item)

      // group by priority
      logicsByPriority[priority] = (
        logicsByPriority[priority] ?? new Set()
      ).add(item.id)

      // add to the cache
      this.logicsCache.set(item.id, item)
    }

    // cache logics grouped and sorted by priority
    this.prioritized = Object.keys(logicsByPriority)
      .sort((a, b) => {
        // sorts from higher to lower
        return Number(b) - Number(a)
      })
      .map((k) => logicsByPriority[k])
  }

  /**
   * Runs the comparison between the fact and the comparison value, while guaranteeing the
   * fact value adheres to the Operator schema. If not, the comparsion will fail.
   */
  static async compare<A = unknown>(
    operator: Operator,
    factValue: unknown,
    comparisonValue: ComparisonValue,
    context: EnrichedRuntimeContext,
    args?: A
  ): Promise<boolean> {
    try {
      const parsedValue = operator.parse(factValue)

      return operator.evaluate(parsedValue, comparisonValue, context, args)
    } catch (originalError) {
      if (!(originalError instanceof ZodError)) {
        const error = new Error(
          `Failed to evaluate operator '${operator.name}'`
        )

        error.cause = originalError

        // eslint-disable-next-line no-console
        console.error(error, { factValue, comparisonValue, args })
      }

      return false
    }
  }

  protected async evaluateLogic(
    logic: Logic,
    facts: RuntimeFacts,
    context: RuntimeContext
  ): Promise<LogicEvent | undefined> {
    try {
      const matches = await pOne(logic.conditions, async (orClauses) => {
        return pEvery(orClauses, async (clause) => {
          return this.evaluateCondition(clause, facts, context)
        })
      })

      if (matches) {
        return Engine.executor(logic.action).event(logic, context)
      }

      return undefined
    } catch (originalError) {
      const error = new Error('Error while evaluating logic')

      error.cause = originalError

      // eslint-disable-next-line no-console
      console.error(error, { logic, facts, context })

      return undefined
    }
  }

  protected async evaluateCondition(
    clause: Clause,
    facts: RuntimeFacts,
    context: RuntimeContext
  ): Promise<boolean> {
    const operator = this.operator(clause.operator, clause.functionName)
    const factValue = Engine.resolveFactValue(clause.fact, facts)

    return Engine.compare<Clause['args']>(
      operator,
      factValue,
      clause.value,
      {
        ...context,
        fact: clause.fact
      },
      clause.args
    )
  }

  static resolveFactValue(fact: Clause['fact'], values: RuntimeFacts): unknown {
    let path: string

    if (isString(fact)) {
      path = fact
    } else if (isBlockFact(fact)) {
      path =
        'path' in fact && typeof fact.path === 'string'
          ? `$.blocks.${fact.referenceId}.${fact.path}`
          : `$.blocks.${fact.referenceId}`
    } else if (isContextParameterFact(fact)) {
      path = `$.contextParameters.${fact.referenceId}`
    } else {
      throw new Error("Failed to determine fact's path")
    }

    return Engine.resolvePath(path, values)
  }

  static resolvePath(path: string, facts: RuntimeFacts): unknown | undefined {
    return JSONPath({
      path,
      json: facts,
      eval: 'safe',
      wrap: false
    })
  }

  /**
   * Simply returns a given logic by its Id from the instance cache.
   */
  getLogic(id: LogicId): Logic | undefined {
    return this.logicsCache?.get(id)
  }

  /**
   * Registers a custom function to the engine.
   * Allows programatically adding custom logic functions in runtime.
   */
  addFunction(name: string, fn: FunctionHandler) {
    this.customFunctions.set(
      name,
      new CustomFunction(name, fn, this.options.operatorTimeoutMs)
    )
  }

  /**
   * Asynchronously runs the engine against the provided facts.
   * The process is done in parallel according to the concurrency limit.
   */
  async run(
    trigger: LOGIC_TRIGGER,
    facts: RuntimeFacts,
    context: RuntimeContext
  ): Promise<LogicEvent[]> {
    const limit = pLimit(this.options.concurrency)
    const orderedEvents: LogicEvent[] = []

    for (const prioritizedSet of this.prioritized) {
      const setQueue: Promise<LogicEvent | undefined>[] = []

      for (const logicId of prioritizedSet) {
        const logic = this.getLogic(logicId)

        if (!logic || logic.triggeredOn !== trigger) continue

        setQueue.push(
          // evaluate in parallel according to concurrency limit
          limit(() => this.evaluateLogic(logic, facts, context))
        )
      }

      orderedEvents.push(
        ...(await Promise.all(setQueue)).filter((event) => !!event)
      )
    }

    return orderedEvents
  }
}
